import React from 'react';
import './../../ff001-starstruck-lines/ff001-starstruck.css';
import { wrap, getSymbolCountsFromWindow, generateWindowFromReelStrips } from './../../../helpers';
import { Reel } from './../../reel/reel';
import { ICoin } from './../../ff001-starstruck-lines/interfaces';
import { IScenarioDB } from './../interfaces';
import * as coinHelpers from './../../ff001-starstruck-lines/coin-spins/helpers';
import CoinSpins from './../../ff001-starstruck-lines/coin-spins/coin-spins';
import {BreakingWays} from './../breaking-ways/breaking-ways';
import { ensureMinimumCoinSpinRows } from '../scenario-request';
import { BigWin } from './../../big-win/big-win';

function Spin(props: { config: any; scenario: IScenarioDB, index: number; isLastSpin: boolean; minimumCoinSpinRows?: number; showCoinShieldWarning?: boolean; allowForceCoinSpinsTrigger?: boolean; onScenarioUpdated: (scenario: IScenarioDB, i: number) => unknown }) {
  const [config] = React.useState(props.config);
  const [window, setWindow] = React.useState<string[][]>([]);
  const symbolCounts = getSymbolCountsFromWindow(window);
  const showCoinShieldWarning = props.showCoinShieldWarning &&
    (symbolCounts.coin ?? 0) >= config.coinFeature.coinTrigger &&
    (symbolCounts.shield ?? 0) > 0;
  const updateScenario = (params: Partial<IScenarioDB>) => {
    const newScenario = {...props.scenario, ...params};
    props.onScenarioUpdated(newScenario, props.index);
  }
  const setWildMultiplier = (reelIndex: number, rowIndex: number, value: number) => {
    const options = props.config.wildMultipliers?.freespins?.values;
    if (props.index === 0 || !Array.isArray(options) || !options.includes(value)) { return; }

    const wildMultipliers = Array.from(
      { length: config.reelsSetup.length },
      (_, currentReelIndex) => {
        const existing = props.scenario.wildMultipliers?.[currentReelIndex] ?? [];
        const rowCount = Math.max(config.reelsSetup[currentReelIndex], existing.length);
        return Array.from(
          { length: rowCount },
          (_, currentRowIndex) => existing[currentRowIndex] ?? options[0],
        );
      },
    );
    wildMultipliers[reelIndex][rowIndex] = value;
    updateScenario({ wildMultipliers });
  }
  const setReelIndex = (params: {reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => {
    const { reelIndex, dir, forcedIndex } = params;
    const reelIndexes = props.scenario.reelIndexes;
    const reels = config.baseSpinsReels[reelIndex];
    const n = (typeof forcedIndex === 'number') ? forcedIndex : reelIndexes[reelIndex] + (1 * dir!);
    const c = wrap(n, reels);
    const w = c < 0 ? (reels.length + c) : c;
    updateScenario({reelIndexes: reelIndexes.map((x, i) => i === reelIndex ? w : x) });
  }
  const onCoinSpinsWindowsUpdated = (coinSpins: (ICoin | null)[][][]) => {
    updateScenario({otherData: {...props.scenario!.otherData, coinSpins, coinSpinIndex: 0 }});
  }
  const onBrokenWindowUpdated = (windowBroken: string[][]) => {
    updateScenario({otherData: {...props.scenario!.otherData, windowBroken }});
  }
  React.useEffect(() => {
    const t = setTimeout(() => {
      const forced = props.allowForceCoinSpinsTrigger && props.scenario.otherData?.forceCoinSpinsTrigger === true;
      const w = getSymbolCountsFromWindow(window);
      const coin = w['coin'] ?? 0;
      const triggerCount = config.coinFeature.coinTrigger ?? 100000; if (triggerCount===100000) { throw new Error('CoinTrigger isnt found the config')}
      if (coin >= triggerCount || (forced && coin > 0)) {
        const firstScenarioWindow = props.scenario?.otherData?.coinSpins?.[0];
        const hasMinimumRows = !props.minimumCoinSpinRows || firstScenarioWindow?.every(
          reel => reel.length >= props.minimumCoinSpinRows!,
        );
        if (hasMinimumRows && coinHelpers.coinWindowsMatch(window, firstScenarioWindow)) { return; }
        console.log(`creating new coin spins game coins: ${coin} spins: ${config.coinFeature.coinTriggerSpinCount}`);
        const coins = coinHelpers.getCoins(props.config);
        const firstWindow = ensureMinimumCoinSpinRows(
          window.map(reel => reel.map(symbol => symbol === 'coin'
            ? {...coins[0].coin, startWindowIndex: 0}
            : null)),
          props.minimumCoinSpinRows || 0,
        );
        onCoinSpinsWindowsUpdated([firstWindow]);
      } else {
        onCoinSpinsWindowsUpdated([]);
      }
    }, 250);
    return () => { clearTimeout(t); }
  }, [window, props.scenario.otherData?.forceCoinSpinsTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const reels = config.reelsSetup.length;
    const rows = config.reelsSetup[0];
    const w = generateWindowFromReelStrips({
      reels,
      rows,
      reelIndexes: props.scenario.reelIndexes,
      reelStrips: props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels,
    });
    setWindow(w);
  }, [props.scenario.reelIndexes]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return <div className={`spin ${props.index > 0 ? 'spin-freespins' : ''}`}>
    <div className='spin-index-num'>{props.index}</div>
    <BigWin
      config={config}
      reelStrips={props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels}
      rowsByReel={config.reelsSetup}
      reelIndexes={props.scenario.reelIndexes}
      mode={props.index > 0 ? 'Free Spins' : 'Base'}
      showFreespins={props.index === 0}
      onApply={reelIndexes => updateScenario({ reelIndexes })}
    />
    <div className={'reelContainer'}>
      {props.scenario.reelIndexes.map((ri, i) => <Reel 
        reelIndex={ri} 
        setReelIndex={ (props: {reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => setReelIndex(props) } 
        reelStrips={(props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels)[i]} 
        index={i} 
        rows={config.reelsSetup[i]} 
        key={`reel-${props.index}-${i}`}/>)}
    </div>
    {props.index > 0 && Array.isArray(config.wildMultipliers?.freespins?.values) &&
      <div className={'reelContainer'} style={{marginTop: 5}}>
        {window.map((reel, reelIndex) => (
          <div
            key={`wild-multipliers-${reelIndex}`}
            style={{width: 50, margin: '0 10px', display: 'flex', flexDirection: 'column', alignItems: 'center'}}
          >
            {reel.map((symbol, rowIndex) => {
              if (symbol !== config.wild) { return null; }
              const options = config.wildMultipliers.freespins.values as number[];
              const value = props.scenario.wildMultipliers?.[reelIndex]?.[rowIndex] ?? options[0];
              return <div key={`wild-multiplier-${reelIndex}-${rowIndex}`} style={{marginTop: 2, marginBottom: 2}}>
                <div style={{fontSize: 8, textAlign: 'center', color: '#aaa'}}>Row {rowIndex + 1}</div>
                <select
                  aria-label={`Wild multiplier reel ${reelIndex + 1} row ${rowIndex + 1}`}
                  style={{fontSize: 10, width: 45}}
                  value={value}
                  onChange={(event) => setWildMultiplier(reelIndex, rowIndex, Number(event.target.value))}
                >
                  {options.map(option => <option key={option} value={option}>{option}x</option>)}
                </select>
              </div>;
            })}
          </div>
        ))}
      </div>}
    <BreakingWays window={window} windowBroken={props.scenario?.otherData?.windowBroken || []} onWindowBrokenUpdated={onBrokenWindowUpdated} config={props.config} />
    {props.allowForceCoinSpinsTrigger && <div style={{ margin: '12px 0' }}>
      <label>
        <input
          type="checkbox"
          checked={props.scenario.otherData?.forceCoinSpinsTrigger === true}
          onChange={event => updateScenario({ otherData: {
            ...props.scenario.otherData,
            forceCoinSpinsTrigger: event.target.checked,
          } })}
        />{' '}Force Coin Spins trigger (1–5 coins)
      </label>
      <div>Triggers when 1–5 coins remain after all shields resolve. Add Coin Spins steps below to script the feature.</div>
    </div>}
    {showCoinShieldWarning && (
      <div className="ff010-coin-shield-warning" role="alert">
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
        <span>Shield results can add reel positions, shifting Coins below them in the Coin Spins grid.</span>
      </div>
    )}
    <CoinSpins window={window} coinSpinsWindow={props.scenario?.otherData?.coinSpins || []} onCoinSpinsWindowsUpdated={onCoinSpinsWindowsUpdated} config={props.config} />
  
  </div>
}
export default Spin;
